// src/components/stickers/UploadOwnImage/UploadOwnImage.jsx
//
// «ЗАГРУЖУ СВОЮ КАРТИНКУ» (п.89, сессия 2026-10-29).
//
// ═══════════════════════════════════════════════════════════════════
// 🔑 ЧТО ЗДЕСЬ ПРОИСХОДИТ, ПО ШАГАМ:
//   1. клиент выбирает файл;
//   2. ДО отправки проверяем размер и формат (не гнать 20 МБ ради отказа);
//   3. спрашиваем то же, что и для каталога: размер, количество, права;
//   4. создаём в корзине позицию «своя картинка» и сразу грузим файл;
//   5. если клиент ещё не решил — «макет ждём» (решение владельца:
//      заказ уходит, потому что мы не знаем, что печатать).
//
// 🔴 ПРАВА НА ИЗОБРАЖЕНИЕ — НЕ ФОРМАЛЬНОСТЬ.
//    `STICKERS.md` §6: «мы продаём печать, а не картинки». Клиент
//    подтверждает, что права есть; отметка сохраняется на сервере
//    (`rights_confirmed_at`). Без неё позицию нельзя оформить.
// ═══════════════════════════════════════════════════════════════════
import React, { useRef, useState } from 'react';
import {
  validateStickerImage,
  checkLightEdges,
  formatSize,
  STICKER_IMAGE_ACCEPT,
} from '../../../utils/imageUpload.js';

const SIZES = [
  { mm: 50, label: '5 см' },
  { mm: 70, label: '7 см' },
  { mm: 100, label: '10 см' },
];

/**
 * @param {object} props
 * @param {(payload: {file: File|null, sizeMm: number, qty: number, pending: boolean}) => Promise<void>} props.onSubmit
 * @param {boolean} props.isBusy
 * @param {string} props.error
 */
export const UploadOwnImage = ({ onSubmit, isBusy, error }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [sizeMm, setSizeMm] = useState(70);
  const [qty, setQty] = useState(1);
  const [rights, setRights] = useState(false);
  const [fileError, setFileError] = useState('');
  const [lightWarning, setLightWarning] = useState(null);

  const handleFile = async (f) => {
    if (!f) return;
    setFileError('');
    setLightWarning(null);
    const check = validateStickerImage(f);
    if (!check.ok) {
      setFileError(check.error);
      // Сброс input: без этого повторный выбор ТОГО ЖЕ файла не вызовет
      // onChange, и клиент решит, что кнопка не работает.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(f);
    // 🔑 Предупреждение, а НЕ отказ: белых чернил нет, и белые детали
    //    на белом фоне не напечатаются. Честнее сказать заранее.
    const warn = await checkLightEdges(f);
    setLightWarning(warn);
  };

  const canSubmit = rights && (file !== null) && !isBusy;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={STICKER_IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          style={{
            width: '100%',
            padding: '22px 16px',
            borderRadius: 12,
            border: '1.5px dashed rgb(var(--c-border))',
            background: 'rgb(var(--c-surface-2))',
            color: 'rgb(var(--c-text))',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-stickers-body)',
          }}
        >
          Приложить картинку
          <div style={{ marginTop: 4, fontSize: 11.5, fontWeight: 400, color: 'rgb(var(--c-muted))' }}>
            PNG, JPG, WEBP или TIFF · до 20 МБ
          </div>
        </button>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderRadius: 12,
            border: '1px solid rgb(var(--c-border))',
            background: 'rgb(var(--c-surface-2))',
            padding: '10px 12px',
            fontSize: 12.5,
          }}
        >
          <img
            src={URL.createObjectURL(file)}
            alt="Выбранная картинка"
            style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'contain', background: '#fff', flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </div>
            <div style={{ color: 'rgb(var(--c-muted))', fontSize: 11.5 }}>{formatSize(file.size)}</div>
          </div>
          <button
            type="button"
            onClick={() => { setFile(null); setLightWarning(null); }}
            style={{ background: 'none', border: 'none', color: 'rgb(var(--c-danger))', fontSize: 12, cursor: 'pointer' }}
          >
            Заменить
          </button>
        </div>
      )}

      {fileError && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'rgb(var(--c-danger))', lineHeight: 1.45 }}>{fileError}</div>
      )}
      {lightWarning && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            borderRadius: 8,
            background: 'rgb(var(--c-accent-soft))',
            color: 'rgb(var(--c-text-secondary))',
            fontSize: 11.5,
            lineHeight: 1.45,
          }}
        >
          {lightWarning}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgb(var(--c-muted))' }}>
          Размер
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {SIZES.map((s) => (
            <button
              key={s.mm}
              type="button"
              onClick={() => setSizeMm(s.mm)}
              style={{
                borderRadius: 8,
                border: `1px solid ${sizeMm === s.mm ? 'rgb(var(--c-accent))' : 'rgb(var(--c-border))'}`,
                background: sizeMm === s.mm ? 'rgb(var(--c-accent))' : 'transparent',
                color: sizeMm === s.mm ? '#fff' : 'rgb(var(--c-text-secondary))',
                padding: '6px 12px',
                fontSize: 11.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-stickers-body)',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ marginBottom: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgb(var(--c-muted))' }}>
          Количество
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid rgb(var(--c-border))', borderRadius: 8, overflow: 'hidden' }}>
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="меньше"
            style={{ width: 32, height: 32, border: 'none', background: 'transparent', color: 'rgb(var(--c-text-secondary))', fontWeight: 700, cursor: 'pointer' }}>
            −
          </button>
          <b style={{ width: 48, display: 'grid', placeItems: 'center', borderLeft: '1px solid rgb(var(--c-border))', borderRight: '1px solid rgb(var(--c-border))', fontSize: 12.5 }}>
            {qty}
          </b>
          <button type="button" onClick={() => setQty((q) => q + 1)} aria-label="больше"
            style={{ width: 32, height: 32, border: 'none', background: 'transparent', color: 'rgb(var(--c-text-secondary))', fontWeight: 700, cursor: 'pointer' }}>
            +
          </button>
        </div>
      </div>

      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, fontSize: 11.5, lineHeight: 1.5, color: 'rgb(var(--c-text-secondary))', cursor: 'pointer' }}>
        <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>У меня есть права на это изображение. Ответственность за права несёт тот, кто загрузил.</span>
      </label>

      {error && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'rgb(var(--c-danger))', lineHeight: 1.45 }}>{error}</div>
      )}

      {/* Два пути — и оба честные:
          • с картинкой — позиция «uploaded», её сразу можно печатать;
          • «макет ждём» — заказ уйдёт, но в нём будет сказано, что
            картинки ещё нет (решение владельца 2026-10-29). */}
      <div style={{ marginTop: 14 }}>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => onSubmit({ file, sizeMm, qty, pending: false })}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: 9,
            padding: 11,
            fontSize: 13.5,
            fontWeight: 700,
            color: '#fff',
            background: 'rgb(var(--c-accent))',
            opacity: canSubmit ? 1 : 0.45,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-stickers-body)',
          }}
        >
          {file ? 'Добавить в корзину' : 'Сначала выберите картинку'}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={() => onSubmit({ file: null, sizeMm, qty, pending: true })}
          style={{
            width: '100%',
            marginTop: 8,
            borderRadius: 9,
            padding: 10,
            fontSize: 12.5,
            fontWeight: 600,
            color: 'rgb(var(--c-text))',
            background: 'rgb(var(--c-surface))',
            border: '1px solid rgb(var(--c-border))',
            cursor: isBusy ? 'wait' : 'pointer',
            fontFamily: 'var(--font-stickers-body)',
          }}
        >
          Картинки пока нет — оформим «макет ждём»
        </button>
        <div style={{ marginTop: 8, fontSize: 11, color: 'rgb(var(--c-muted))', lineHeight: 1.5 }}>
          «Макет ждём» — заказ оформится, но печать начнётся, когда вы пришлёте картинку. Мы напомним.
        </div>
      </div>
    </div>
  );
};
